import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Skeleton } from './ui/skeleton';
import { Avatar, AvatarFallback } from './ui/avatar';
import { 
  Star, 
  ShieldCheck, 
  Building2, 
  Zap, 
  Trophy, 
  Award,
  Sparkles,
  ThumbsUp,
  MessageSquare,
  ChevronRight
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { useTranslation } from '../i18n';

// Badge icon mapping
const badgeIcons = {
  'shield-check': ShieldCheck,
  'building-2': Building2,
  'star': Star,
  'zap': Zap,
  'trophy': Trophy,
  'award': Award,
  'sparkles': Sparkles
};

// Badge color mapping
const badgeColors = {
  blue: 'bg-blue-100 text-blue-700 border-blue-200',
  green: 'bg-green-100 text-green-700 border-green-200',
  amber: 'bg-amber-100 text-amber-700 border-amber-200',
  purple: 'bg-purple-100 text-purple-700 border-purple-200',
  teal: 'bg-teal-100 text-teal-700 border-teal-200',
  gray: 'bg-gray-100 text-gray-700 border-gray-200'
};

// Star Rating Display
export function StarRating({ rating, size = 'md', showValue = true }) {
  const sizes = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5'
  };
  
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <Star
        key={i}
        className={`${sizes[size]} ${i <= rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`}
      />
    );
  }
  
  return (
    <div className="flex items-center gap-1">
      <div className="flex">{stars}</div>
      {showValue && <span className="text-sm font-medium ml-1">{rating.toFixed(1)}</span>}
    </div>
  );
}

// Star Rating Input
export function StarRatingInput({ value, onChange }) {
  const [hover, setHover] = useState(0);
  
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          className="p-1 hover:scale-110 transition-transform"
        >
          <Star
            className={`h-8 w-8 ${
              star <= (hover || value)
                ? 'text-amber-400 fill-amber-400'
                : 'text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
}

// Trust Badge Component
export function TrustBadge({ badge, size = 'md' }) {
  const Icon = badgeIcons[badge.icon] || Award;
  const colorClass = badgeColors[badge.color] || badgeColors.gray;
  
  const sizes = {
    sm: 'text-xs py-0.5 px-2',
    md: 'text-sm py-1 px-2.5',
    lg: 'text-sm py-1.5 px-3'
  };
  
  const iconSizes = {
    sm: 'h-3 w-3',
    md: 'h-3.5 w-3.5',
    lg: 'h-4 w-4'
  };
  
  return (
    <span 
      className={`inline-flex items-center gap-1.5 rounded-full border font-medium ${colorClass} ${sizes[size]}`}
      title={badge.description}
    >
      <Icon className={iconSizes[size]} />
      {badge.name}
    </span>
  );
}

// Trust Badges List
export function TrustBadgesList({ userId, className = '' }) {
  const { data, isLoading } = useQuery({
    queryKey: ['user-badges', userId],
    queryFn: () => api.get(`/reviews/badges/user/${userId}`),
    enabled: !!userId
  });
  
  if (isLoading) {
    return (
      <div className={`flex gap-2 ${className}`}>
        <Skeleton className="h-6 w-24" />
        <Skeleton className="h-6 w-20" />
      </div>
    );
  }
  
  const badges = data?.badges || [];
  
  if (badges.length === 0) return null;
  
  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {badges.map((badge) => (
        <TrustBadge key={badge._id} badge={badge} size="sm" />
      ))}
    </div>
  );
}

// Rating Stats Component
export function RatingStats({ stats, compact = false }) {
  if (!stats) return null;
  
  const distribution = stats.rating_distribution || {};
  const total = stats.total_reviews || 0;
  
  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <StarRating rating={stats.average_rating || 0} size="sm" />
        <span className="text-sm text-muted-foreground">({total})</span>
      </div>
    );
  }
  
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="text-4xl font-bold">{(stats.average_rating || 0).toFixed(1)}</div>
        <div>
          <StarRating rating={stats.average_rating || 0} size="lg" showValue={false} />
          <p className="text-sm text-muted-foreground">{total} reviews</p>
        </div>
      </div>
      
      <div className="space-y-1.5">
        {[5, 4, 3, 2, 1].map((star) => {
          const count = distribution[star] || 0;
          const percentage = total > 0 ? (count / total) * 100 : 0;
          
          return (
            <div key={star} className="flex items-center gap-2 text-sm">
              <span className="w-3">{star}</span>
              <Star className="h-3 w-3 text-amber-400 fill-amber-400" />
              <Progress value={percentage} className="flex-1 h-2" />
              <span className="w-8 text-right text-muted-foreground">{count}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Single Review Card
export function ReviewCard({ review, onHelpful }) {
  const [marking, setMarking] = useState(false);
  
  const handleHelpful = async () => {
    if (marking) return;
    setMarking(true);
    try {
      await onHelpful(review._id);
      toast.success('Marked as helpful');
    } catch (err) {
      toast.error(err.message || 'Could not mark as helpful');
    } finally {
      setMarking(false);
    }
  };
  
  return (
    <div className="border-b pb-4 last:border-0 last:pb-0">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10">
            <AvatarFallback>
              {review.reviewer_name?.charAt(0) || '?'}
            </AvatarFallback>
          </Avatar>
          <div>
            <p className="font-medium">{review.reviewer_name}</p>
            <StarRating rating={review.rating} size="sm" showValue={false} />
          </div>
        </div>
        <span className="text-xs text-muted-foreground">
          {new Date(review.created_at).toLocaleDateString()}
        </span>
      </div>
      
      {review.title && (
        <h4 className="font-medium mb-1">{review.title}</h4>
      )}
      
      {review.comment && (
        <p className="text-sm text-muted-foreground mb-3">{review.comment}</p>
      )}
      
      {review.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {review.tags.map((tag) => (
            <Badge key={tag} variant="secondary" className="text-xs">
              {tag.replace(/_/g, ' ')}
            </Badge>
          ))}
        </div>
      )}
      
      <Button
        variant="ghost"
        size="sm"
        onClick={handleHelpful}
        disabled={marking}
        className="text-xs text-muted-foreground"
      >
        <ThumbsUp className="h-3 w-3 mr-1" />
        Helpful ({review.helpful_count || 0})
      </Button>
    </div>
  );
}

// Reviews List Component
export function ReviewsList({ userId }) {
  const { data, isLoading } = useQuery({
    queryKey: ['user-reviews', userId],
    queryFn: () => api.get(`/reviews/user/${userId}`),
    enabled: !!userId
  });
  
  const queryClient = useQueryClient();
  
  const handleHelpful = async (reviewId) => {
    await api.post(`/reviews/${reviewId}/helpful`);
    queryClient.invalidateQueries(['user-reviews', userId]);
  };
  
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ))}
      </div>
    );
  }
  
  const reviews = data?.reviews || [];
  const stats = data?.stats;
  
  return (
    <div className="space-y-6">
      <RatingStats stats={stats} />
      
      {reviews.length === 0 ? (
        <p className="text-center text-muted-foreground py-8">
          No reviews yet
        </p>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <ReviewCard 
              key={review._id} 
              review={review} 
              onHelpful={handleHelpful}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Write Review Dialog
export function WriteReviewDialog({
  targetUserId, targetUserName, dealId, open, onOpenChange }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  
  const { data: tagsData } = useQuery({
    queryKey: ['review-tags'],
    queryFn: () => api.get('/reviews/tags')
  });
  
  const submitReview = useMutation({
    mutationFn: (data) => api.post('/reviews', data),
    onSuccess: () => {
      queryClient.invalidateQueries(['user-reviews', targetUserId]);
      toast.success('Review submitted');
      onOpenChange(false);
      // Reset form
      setRating(0);
      setTitle('');
      setComment('');
      setSelectedTags([]);
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    }
  });
  
  const handleSubmit = () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }
    
    submitReview.mutate({
      target_user_id: targetUserId,
      deal_id: dealId,
      rating,
      title: title || undefined,
      comment: comment || undefined,
      tags: selectedTags
    });
  };
  
  const toggleTag = (tagId) => {
    setSelectedTags((prev) =>
      prev.includes(tagId)
        ? prev.filter((t) => t !== tagId)
        : [...prev, tagId]
    );
  };
  
  const tags = tagsData?.tags || [];
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Write a Review</DialogTitle>
          <CardDescription>
            Share your experience with {targetUserName}
          </CardDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="text-center">
            <Label className="mb-2 block">Rating</Label>
            <StarRatingInput value={rating} onChange={setRating} />
          </div>
          
          <div className="space-y-2">
            <Label>Title (optional)</Label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Summarize your experience"
              className="w-full px-3 py-2 border rounded-md"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Review</Label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Tell others about your experience..."
              rows={4}
            />
          </div>
          
          <div className="space-y-2">
            <Label>Tags (select all that apply)</Label>
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => (
                <Badge
                  key={tag.id}
                  variant={selectedTags.includes(tag.id) ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => toggleTag(tag.id)}
                >
                  {tag.label}
                </Badge>
              ))}
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitReview.isPending}>
            {submitReview.isPending ? 'Submitting...' : 'Submit Review'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Profile Review Summary (for directory/profile cards)
export function ProfileReviewSummary({ userId, showBadges = true }) {
  const { data: reviewData } = useQuery({
    queryKey: ['user-reviews', userId],
    queryFn: () => api.get(`/reviews/user/${userId}`),
    enabled: !!userId
  });
  
  const stats = reviewData?.stats;
  
  return (
    <div className="space-y-2">
      {stats && stats.total_reviews > 0 && (
        <RatingStats stats={stats} compact />
      )}
      {showBadges && <TrustBadgesList userId={userId} />}
    </div>
  );
}

export default {
  StarRating,
  StarRatingInput,
  TrustBadge,
  TrustBadgesList,
  RatingStats,
  ReviewCard,
  ReviewsList,
  WriteReviewDialog,
  ProfileReviewSummary
};
